import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Collapse, Empty, Input, InputNumber, Select, Spin, Switch, Upload, message } from 'antd'
import { notify } from 'reapop'
import { useDispatch } from 'react-redux'
import moment from 'moment'
import update from 'immutability-helper'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

import {
  fetchConfigurationByKey,
  getConfigurationList,
  updateConfigurationMetadataByKey,
  updateConfiguration,
} from '../../../api/configuration'
import { fetchContactInfo, updateContactInfo } from '../../../api/contact'
import {
  fetchLovChildrenByParentName,
  updateLov,
  uploadLovIcon,
  batchUpdateLovOrder,
} from '../../../api/lov'
import {
  fetchLoyaltyReferralConfig,
  updateLoyaltyReferralConfig,
} from '../../../api/loyalty'
import {
  getCatalogConfiguration,
  updateCatalogConfiguration,
  syncCatalog,
  validatePlatformConfiguration,
  CatalogPlatform,
} from '../../../api/product-catalog'
import { DATE_TIME_FORMAT, ICONS, PERMISSIONS } from '../../../utils/constants'

import useClient from '../../../hooks/useClient'
import { useDebounce } from '../../../hooks/useDebounce'

import PageTable from '../../../components/PageTable'
import Icon from '../../../components/Icon'
import Button from '../../../components/Button'
import SortableColumnTitle from '../../../components/SortableColumnTitle'

import styles from './Configuration.module.scss'

const { Panel } = Collapse
const { Option } = Select
const { Search } = Input

const PAYMENT_METHOD_CONFIGURATION_KEY = 'PAYMENT_CONFIGURATION'
const LOV_KEY = 'PAYMENT_METHODS'
const PAYMENT_CONTEXT_FLAG_MAP = {
  order: 'isOrder',
  coupon: 'isCoupon',
  wallet: 'isWallet',
  mosque: 'isMosque',
  subscription: 'isSubscription',
}
const PAYMENT_CONTEXT_KEYS = Object.keys(PAYMENT_CONTEXT_FLAG_MAP)
const WALLET_CONFIGURATION_KEY = 'WALLET_CONFIGURATION'
const CHECKOUT_CONFIGURATION_KEY = 'CHECKOUT_CONFIGURATION'
const MARKETING_EMAIL_KEY = 'MARKETING_EMAIL'
const LOYALTY_REFERRAL_KEY = 'LOYALTY_REFERRAL'
const PRODUCT_CATALOG_KEY = 'PRODUCT_CATALOG'

const buildCatalogConfigurationState = (config = {}) => ({
  meta: {
    enabled: config?.meta?.enabled || false,
    businessId: config?.meta?.businessId || '',
    catalogId: config?.meta?.catalogId || '',
    accessToken: config?.meta?.accessToken || '',
    storeUrl: config?.meta?.storeUrl || '',
    brandName: config?.meta?.brandName || 'Abraaj Water',
  },
  google: {
    enabled: config?.google?.enabled || false,
    merchantId: config?.google?.merchantId || '',
    serviceAccountCredentials: config?.google?.serviceAccountCredentials || '',
    storeUrl: config?.google?.storeUrl || '',
    brandName: config?.google?.brandName || 'Abraaj Water',
  },
})

const WALLET_LIMIT_FIELDS = [
  {
    key: 'walletAmount',
    titleKey: 'configuration.wallet.fields.walletAmount.title',
    descriptionKey: 'configuration.wallet.fields.walletAmount.description',
  },
  {
    key: 'topupAmount',
    titleKey: 'configuration.wallet.fields.topupAmount.title',
    descriptionKey: 'configuration.wallet.fields.topupAmount.description',
  },
  {
    key: 'checkoutAmount',
    titleKey: 'configuration.wallet.fields.checkoutAmount.title',
    descriptionKey: 'configuration.wallet.fields.checkoutAmount.description',
  },
]

const normalizeWalletLimit = (limit) => ({
  min: typeof limit?.min === 'number' ? limit.min : 0,
  max: typeof limit?.max === 'number' ? limit.max : 0,
})

const buildWalletConfigurationState = (metadata = {}) => ({
  walletAmount: normalizeWalletLimit(metadata?.walletAmount),
  topupAmount: normalizeWalletLimit(metadata?.topupAmount),
  checkoutAmount: normalizeWalletLimit(metadata?.checkoutAmount),
})

const buildCheckoutConfigurationState = (metadata = {}) => ({
  checkoutMinAmount: typeof metadata?.checkoutMinAmount === 'number' ? metadata.checkoutMinAmount : 0,
  deliveryDateMessage: {
    en: metadata?.deliveryDateMessage?.en || '',
    ar: metadata?.deliveryDateMessage?.ar || '',
  },
})

const buildLoyaltyReferralConfigurationState = (metadata = {}) => {
  const loyalty = metadata?.loyalty || {}
  const referral = metadata?.referral || {}
  
  const earnPointsPerKwd =
    typeof loyalty?.pointValuePerKwd === 'number' && loyalty.pointValuePerKwd > 0
      ? Math.round(1 / loyalty.pointValuePerKwd)
      : 10
  const redemptionPointsPerKwd =
    typeof loyalty?.redemptionPointValuePerKwd === 'number' && loyalty.redemptionPointValuePerKwd > 0
      ? Math.round(1 / loyalty.redemptionPointValuePerKwd)
      : earnPointsPerKwd

  return {
    loyaltyEnabled: typeof loyalty?.enabled === 'boolean' ? loyalty.enabled : false,
    pointsPerKwd: earnPointsPerKwd,
    redemptionPointsPerKwd,
    earnPointMinOrderValue: typeof loyalty?.earnPointMinOrderValue === 'number' ? loyalty.earnPointMinOrderValue : 3.5,
    minimumRedeemPointsForOrder: typeof loyalty?.minimumRedeemPointsForOrder === 'number' ? loyalty.minimumRedeemPointsForOrder : 200,
    pointsExpiryInMonths: typeof loyalty?.pointsExpiryInMonths === 'number' ? loyalty.pointsExpiryInMonths : 48,
    completeProfilePoints: typeof loyalty?.completeProfilePoints === 'number' ? loyalty.completeProfilePoints : 500,
    maxRedeemPercentage: typeof loyalty?.maxRedeemPercentage === 'number' ? loyalty.maxRedeemPercentage : 50,
    disableLoyaltyWithPromocode: typeof loyalty?.disableLoyaltyWithPromocode === 'boolean' ? loyalty.disableLoyaltyWithPromocode : false,
    referralEnabled: typeof referral?.enabled === 'boolean' ? referral.enabled : false,
    newCustomerPoints: typeof referral?.newCustomerPoints === 'number' ? referral.newCustomerPoints : 300,
    referrerPoints: typeof referral?.referrerPoints === 'number' ? referral.referrerPoints : 500,
    shareMessageEn: referral?.shareMessage?.en || '',
    shareMessageAr: referral?.shareMessage?.ar || '',
  }
}

const Configuration = () => {
  const { t } = useTranslation()
  const { isClientCan } = useClient()
  // const navigate = useNavigate()
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [sortType, setSortType] = useState('createdAt')
  const [sortOrder, setSortOrder] = useState('descend')
  const [keySearch, setKeySearch] = useState('')
  const [activeFilter, setActiveFilter] = useState(null)
  
  // Debounce search input to avoid excessive API calls
  const debouncedKeySearch = useDebounce(keySearch, 500)

  // const isClientCanCreateConfigurations = isClientCan(PERMISSIONS.CREATE_CONFIGURATIONS)
  const isClientCanUpdateConfigurations = isClientCan(PERMISSIONS.UPDATE_CONFIGURATIONS)

  const {
    data: allConfigurationsResponse,
    isFetching: isConfigurationsFetching,
    refetch: refetchConfigurations,
  } = useQuery(
    ['all-configurations'],
    async () => await getConfigurationList({ pageSize: 1000 }),
    {
      retry: 1,
      onError: () => {
        dispatch(
          notify({
            title: t('configuration.messages.loadFailed'),
            status: 'error',
            dismissAfter: 4000,
          }),
        )
      },
    },
  )

  const configurations = allConfigurationsResponse?.result || []

  const paymentConfiguration = useMemo(
    () => configurations.find((c) => c.key === PAYMENT_METHOD_CONFIGURATION_KEY),
    [configurations],
  )

  const walletConfiguration = useMemo(
    () => configurations.find((c) => c.key === WALLET_CONFIGURATION_KEY),
    [configurations],
  )

  const checkoutConfiguration = useMemo(
    () => configurations.find((c) => c.key === CHECKOUT_CONFIGURATION_KEY),
    [configurations],
  )

  const marketingEmailConfiguration = useMemo(
    () => configurations.find((c) => c.key === MARKETING_EMAIL_KEY),
    [configurations],
  )

  const loyaltyReferralConfiguration = useMemo(
    () => configurations.find((c) => c.key === LOYALTY_REFERRAL_KEY),
    [configurations],
  )

  const {
    data: paymentMethods = [],
    isFetching: isPaymentMethodsFetching,
    refetch: refetchPaymentMethods,
  } = useQuery(
    ['lovs', 'children', LOV_KEY],
    async () => await fetchLovChildrenByParentName(LOV_KEY),
    {
      retry: 1,
      onError: () => {
        dispatch(
          notify({
            title: t('configuration.paymentMethods.messages.loadFailed'),
            status: 'error',
            dismissAfter: 4000,
          }),
        )
      },
    },
  )

  const isPaymentConfigurationFetching = false // No longer fetching payment config metadata

  const [paymentConfigurationState] = useState({})

  const updatePaymentMetadataMutation = useMutation(updateLov, {
    onSuccess: () => {
      dispatch(
        notify({
          title: t('configuration.paymentMethods.messages.updated'),
          status: 'success',
          dismissAfter: 2000,
        }),
      )
      refetchPaymentMethods()
    },
    onError: () => {
      dispatch(
        notify({
          title: t('configuration.paymentMethods.messages.updateFailed'),
          status: 'error',
          dismissAfter: 4000,
        }),
      )
      refetchPaymentMethods()
    },
  })

  const batchUpdateLovOrderMutation = useMutation(batchUpdateLovOrder, {
    onSuccess: () => {
      dispatch(
        notify({
          title: t('configuration.paymentMethods.messages.updated'),
          status: 'success',
          dismissAfter: 2000,
        }),
      )
      refetchPaymentMethods()
    },
    onError: () => {
      dispatch(
        notify({
          title: t('configuration.paymentMethods.messages.updateFailed'),
          status: 'error',
          dismissAfter: 4000,
        }),
      )
      refetchPaymentMethods()
    },
  })

  const updatePaymentIconMutation = useMutation(uploadLovIcon, {
    onSuccess: () => {
      dispatch(
        notify({
          title: t('configuration.paymentMethods.messages.updated'),
          status: 'success',
          dismissAfter: 2000,
        }),
      )
      refetchPaymentMethods()
    },
    onError: () => {
      dispatch(
        notify({
          title: t('configuration.paymentMethods.messages.updateFailed'),
          status: 'error',
          dismissAfter: 4000,
        }),
      )
      refetchPaymentMethods()
    },
  })

  const handlePaymentToggle = useCallback(
    (method, contextKey, enabled) => {
      const usageFlagKey = PAYMENT_CONTEXT_FLAG_MAP[contextKey]
      if (!usageFlagKey) {
        return
      }

      const currentFlags = method.usageFlags || {}
      const newFlags = { ...currentFlags, [usageFlagKey]: enabled }

      // Call updateLov API instead of updateConfigurationMetadataByKey
      updatePaymentMetadataMutation.mutate({
        id: method.id,
        usageFlags: newFlags
      })
    },
    [updatePaymentMetadataMutation],
  )

  const handleSortOrderUpdate = useCallback(
    (methodId, sortOrder) => {
      if (typeof sortOrder !== 'number') {
        return
      }

      updatePaymentMetadataMutation.mutate({
        id: methodId,
        sortOrder,
      })
    },
    [updatePaymentMetadataMutation],
  )

  const handleBatchSortOrderUpdate = useCallback(
    (items) => {
      if (!Array.isArray(items) || items.length === 0) {
        return
      }

      batchUpdateLovOrderMutation.mutate(items)
    },
    [batchUpdateLovOrderMutation],
  )

  const handleIconUpload = useCallback(
    (methodId, file) => {
      if (!file) {
        return
      }

      updatePaymentIconMutation.mutate({
        id: methodId,
        file,
      })
    },
    [updatePaymentIconMutation],
  )

  const sortOrderSavingId =
    updatePaymentMetadataMutation.isLoading && updatePaymentMetadataMutation.variables
      ? updatePaymentMetadataMutation.variables.id
      : null

  const iconUploadingId =
    updatePaymentIconMutation.isLoading && updatePaymentIconMutation.variables
      ? updatePaymentIconMutation.variables.id
      : null

  const paymentContextOptions = useMemo(
    () =>
      PAYMENT_CONTEXT_KEYS.map((contextKey) => ({
        key: contextKey,
        label: t(`configuration.paymentContexts.${contextKey}`),
      })),
    [t],
  )

  const isWalletConfigurationFetching = isConfigurationsFetching

  const normalizedWalletConfiguration = useMemo(
    () => buildWalletConfigurationState(walletConfiguration?.metadata),
    [walletConfiguration?.metadata],
  )

  const [walletConfigurationState, setWalletConfigurationState] = useState(normalizedWalletConfiguration)

  useEffect(() => {
    setWalletConfigurationState(normalizedWalletConfiguration)
  }, [normalizedWalletConfiguration])

  const updateWalletConfigurationMutation = useMutation(updateConfigurationMetadataByKey, {
    onSuccess: () => {
      dispatch(
        notify({
          title: t('configuration.wallet.messages.updated'),
          status: 'success',
          dismissAfter: 2000,
        }),
      )
      refetchConfigurations()
    },
    onError: () => {
      dispatch(
        notify({
          title: t('configuration.wallet.messages.updateFailed'),
          status: 'error',
          dismissAfter: 4000,
        }),
      )
    },
  })

  const handleWalletLimitChange = useCallback((fieldKey, boundKey, value) => {
    setWalletConfigurationState((prev) => ({
      ...prev,
      [fieldKey]: {
        ...prev[fieldKey],
        [boundKey]: typeof value === 'number' ? value : prev[fieldKey][boundKey],
      },
    }))
  }, [])

  const handleWalletConfigurationSave = useCallback(() => {
    updateWalletConfigurationMutation.mutate({
      key: WALLET_CONFIGURATION_KEY,
      metadata: walletConfigurationState,
    })
  }, [updateWalletConfigurationMutation, walletConfigurationState])

  const isCheckoutConfigurationFetching = isConfigurationsFetching

  const normalizedCheckoutConfiguration = useMemo(
    () => buildCheckoutConfigurationState(checkoutConfiguration?.metadata),
    [checkoutConfiguration?.metadata],
  )

  const [checkoutConfigurationState, setCheckoutConfigurationState] = useState(normalizedCheckoutConfiguration)

  useEffect(() => {
    setCheckoutConfigurationState(normalizedCheckoutConfiguration)
  }, [normalizedCheckoutConfiguration])

  const updateCheckoutConfigurationMutation = useMutation(updateConfigurationMetadataByKey, {
    onSuccess: () => {
      dispatch(
        notify({
          title: t('configuration.checkout.messages.updated'),
          status: 'success',
          dismissAfter: 2000,
        }),
      )
      refetchConfigurations()
    },
    onError: () => {
      dispatch(
        notify({
          title: t('configuration.checkout.messages.updateFailed'),
          status: 'error',
          dismissAfter: 4000,
        }),
      )
    },
  })

  const handleCheckoutConfigurationChange = useCallback((key, value, subKey) => {
    setCheckoutConfigurationState((prev) => {
      if (subKey) {
        return {
          ...prev,
          [key]: {
            ...prev[key],
            [subKey]: value,
          },
        }
      }
      return {
        ...prev,
        [key]: value,
      }
    })
  }, [])

  const handleCheckoutConfigurationSave = useCallback(() => {
    updateCheckoutConfigurationMutation.mutate({
      key: CHECKOUT_CONFIGURATION_KEY,
      metadata: checkoutConfigurationState,
    })
  }, [updateCheckoutConfigurationMutation, checkoutConfigurationState])

  const isMarketingEmailConfigurationFetching = isConfigurationsFetching

  const [marketingEmailState, setMarketingEmailState] = useState('')

  useEffect(() => {
    setMarketingEmailState(marketingEmailConfiguration?.valueEn || '')
  }, [marketingEmailConfiguration?.valueEn])

  const updateMarketingEmailMutation = useMutation(updateConfiguration, {
    onSuccess: () => {
      dispatch(
        notify({
          title: t('configuration.marketingEmail.messages.updated'),
          status: 'success',
          dismissAfter: 2000,
        }),
      )
      refetchConfigurations()
    },
    onError: () => {
      dispatch(
        notify({
          title: t('configuration.marketingEmail.messages.updateFailed'),
          status: 'error',
          dismissAfter: 4000,
        }),
      )
    },
  })

  const handleMarketingEmailChange = useCallback((value) => {
    setMarketingEmailState(value)
  }, [])

  const handleMarketingEmailSave = useCallback(() => {
    if (!marketingEmailConfiguration?.id) {
      return
    }
    updateMarketingEmailMutation.mutate({
      id: marketingEmailConfiguration.id,
      valueEn: marketingEmailState,
      valueAr: marketingEmailState, // Keep Arabic same as English for email
    })
  }, [updateMarketingEmailMutation, marketingEmailConfiguration, marketingEmailState])

  const isMarketingEmailConfigurationAvailable = Boolean(marketingEmailConfiguration?.id)
  const isMarketingEmailDirty = useMemo(
    () => marketingEmailState !== (marketingEmailConfiguration?.valueEn || ''),
    [marketingEmailState, marketingEmailConfiguration?.valueEn],
  )

  const isLoyaltyReferralConfigurationFetching = isConfigurationsFetching

  // Contact Info
  const { data: contactInfoData, isFetching: isContactInfoFetching, refetch: refetchContactInfo } = useQuery(
    ['contact-info'],
    async () => await fetchContactInfo(),
    {
      retry: 1,
      onError: () => {
        dispatch(
          notify({
            title: t('configuration.contactInfo.messages.loadFailed'),
            status: 'error',
            dismissAfter: 4000,
          }),
        )
      },
    },
  )

  const [contactInfoState, setContactInfoState] = useState({
    address: '',
    phone: '',
    whatsApp: '',
    instagram: '',
    twitter: '',
    email: '',
    facebook: '',
  })

  useEffect(() => {
    setContactInfoState({
      address: contactInfoData?.address || '',
      phone: contactInfoData?.phone || '',
      whatsApp: contactInfoData?.whatsApp || '',
      instagram: contactInfoData?.instagram || '',
      twitter: contactInfoData?.twitter || '',
      email: contactInfoData?.email || '',
      facebook: contactInfoData?.facebook || '',
    })
  }, [
    contactInfoData?.address,
    contactInfoData?.phone,
    contactInfoData?.whatsApp,
    contactInfoData?.instagram,
    contactInfoData?.twitter,
    contactInfoData?.email,
    contactInfoData?.facebook,
  ])

  const updateContactInfoMutation = useMutation(updateContactInfo, {
    onSuccess: () => {
      dispatch(
        notify({
          title: t('configuration.contactInfo.messages.updated'),
          status: 'success',
          dismissAfter: 2000,
        }),
      )
      refetchContactInfo()
    },
    onError: () => {
      dispatch(
        notify({
          title: t('configuration.contactInfo.messages.updateFailed'),
          status: 'error',
          dismissAfter: 4000,
        }),
      )
    },
  })

  const handleContactInfoChange = useCallback((key, value) => {
    setContactInfoState((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleContactInfoSave = useCallback(() => {
    updateContactInfoMutation.mutate({
      address: contactInfoState.address,
      phone: contactInfoState.phone,
      whatsApp: contactInfoState.whatsApp,
      instagram: contactInfoState.instagram,
      twitter: contactInfoState.twitter,
      email: contactInfoState.email,
      facebook: contactInfoState.facebook,
    })
  }, [updateContactInfoMutation, contactInfoState])

  const isContactInfoDirty = useMemo(() => {
    return (
      contactInfoState.address !== (contactInfoData?.address || '') ||
      contactInfoState.phone !== (contactInfoData?.phone || '') ||
      contactInfoState.whatsApp !== (contactInfoData?.whatsApp || '') ||
      contactInfoState.instagram !== (contactInfoData?.instagram || '') ||
      contactInfoState.twitter !== (contactInfoData?.twitter || '') ||
      contactInfoState.email !== (contactInfoData?.email || '') ||
      contactInfoState.facebook !== (contactInfoData?.facebook || '')
    )
  }, [
    contactInfoState,
    contactInfoData?.address,
    contactInfoData?.phone,
    contactInfoData?.whatsApp,
    contactInfoData?.instagram,
    contactInfoData?.twitter,
    contactInfoData?.email,
    contactInfoData?.facebook,
  ])

  const normalizedLoyaltyReferralConfiguration = useMemo(
    () => buildLoyaltyReferralConfigurationState(loyaltyReferralConfiguration?.metadata),
    [loyaltyReferralConfiguration?.metadata],
  )

  const [loyaltyReferralConfigurationState, setLoyaltyReferralConfigurationState] = useState(
    normalizedLoyaltyReferralConfiguration,
  )

  useEffect(() => {
    setLoyaltyReferralConfigurationState(normalizedLoyaltyReferralConfiguration)
  }, [normalizedLoyaltyReferralConfiguration])

  const updateLoyaltyReferralConfigurationMutation = useMutation(updateConfigurationMetadataByKey, {
    onSuccess: () => {
      dispatch(
        notify({
          title: t('configuration.loyaltyReferral.messages.updated'),
          status: 'success',
          dismissAfter: 2000,
        }),
      )
      refetchConfigurations()
    },
    onError: () => {
      dispatch(
        notify({
          title: t('configuration.loyaltyReferral.messages.updateFailed'),
          status: 'error',
          dismissAfter: 4000,
        }),
      )
    },
  })

  const handleLoyaltyReferralConfigurationChange = useCallback((key, value) => {
    setLoyaltyReferralConfigurationState((prev) => ({
      ...prev,
      [key]: value,
    }))
  }, [])

  const shareMessageValidationError = useMemo(() => {
    if (!loyaltyReferralConfigurationState.referralEnabled) return null
    const en = loyaltyReferralConfigurationState.shareMessageEn || ''
    const ar = loyaltyReferralConfigurationState.shareMessageAr || ''
    if (en && !en.includes('{{REFERRAL_CODE}}')) return 'English share message must contain {{REFERRAL_CODE}}'
    if (ar && !ar.includes('{{REFERRAL_CODE}}')) return 'Arabic share message must contain {{REFERRAL_CODE}}'
    return null
  }, [loyaltyReferralConfigurationState.referralEnabled, loyaltyReferralConfigurationState.shareMessageEn, loyaltyReferralConfigurationState.shareMessageAr])

  const handleLoyaltyReferralConfigurationSave = useCallback(() => {
    if (shareMessageValidationError) {
      dispatch(notify({ title: shareMessageValidationError, status: 'error', dismissAfter: 4000 }))
      return
    }
    // Convert flat structure to nested structure
    const metadata = {
      loyalty: {
        enabled: loyaltyReferralConfigurationState.loyaltyEnabled,
        pointValuePerKwd:
          Number(loyaltyReferralConfigurationState.pointsPerKwd) > 0
            ? 1 / Number(loyaltyReferralConfigurationState.pointsPerKwd)
            : 0,
        redemptionPointValuePerKwd: (() => {
          const earn =
            Number(loyaltyReferralConfigurationState.pointsPerKwd) > 0
              ? 1 / Number(loyaltyReferralConfigurationState.pointsPerKwd)
              : 0
          const redeem =
            Number(loyaltyReferralConfigurationState.redemptionPointsPerKwd) > 0
              ? 1 / Number(loyaltyReferralConfigurationState.redemptionPointsPerKwd)
              : 0
          return redeem > 0 ? redeem : earn
        })(),
        earnPointMinOrderValue: loyaltyReferralConfigurationState.earnPointMinOrderValue,
        minimumRedeemPointsForOrder: loyaltyReferralConfigurationState.minimumRedeemPointsForOrder,
        pointsExpiryInMonths: loyaltyReferralConfigurationState.pointsExpiryInMonths,
        completeProfilePoints: loyaltyReferralConfigurationState.completeProfilePoints,
        maxRedeemPercentage: loyaltyReferralConfigurationState.maxRedeemPercentage,
        disableLoyaltyWithPromocode: loyaltyReferralConfigurationState.disableLoyaltyWithPromocode,
      },
      referral: {
        enabled: loyaltyReferralConfigurationState.referralEnabled,
        newCustomerPoints: loyaltyReferralConfigurationState.newCustomerPoints,
        referrerPoints: loyaltyReferralConfigurationState.referrerPoints,
        shareMessage: {
          en: loyaltyReferralConfigurationState.shareMessageEn,
          ar: loyaltyReferralConfigurationState.shareMessageAr,
        },
      },
    }

    updateLoyaltyReferralConfigurationMutation.mutate({
      key: LOYALTY_REFERRAL_KEY,
      metadata,
    })
  }, [updateLoyaltyReferralConfigurationMutation, loyaltyReferralConfigurationState, shareMessageValidationError, dispatch])

  const isLoyaltyReferralConfigurationAvailable = Boolean(loyaltyReferralConfiguration?.id)
  const isLoyaltyReferralConfigurationLoading = isLoyaltyReferralConfigurationFetching
  const isLoyaltyReferralDirty = useMemo(
    () =>
      JSON.stringify(loyaltyReferralConfigurationState) !==
      JSON.stringify(normalizedLoyaltyReferralConfiguration),
    [loyaltyReferralConfigurationState, normalizedLoyaltyReferralConfiguration],
  )

  // Product Catalog Configuration
  const {
    data: catalogConfigurationData,
    isFetching: isCatalogConfigurationFetching,
    refetch: refetchCatalogConfiguration,
  } = useQuery(
    ['catalog-configuration'],
    async () => await getCatalogConfiguration(),
    {
      retry: 1,
      onError: () => {
        dispatch(
          notify({
            title: t('configuration.productCatalog.messages.loadFailed'),
            status: 'error',
            dismissAfter: 4000,
          }),
        )
      },
    },
  )


  const normalizedCatalogConfiguration = useMemo(
    () => buildCatalogConfigurationState(catalogConfigurationData),
    [catalogConfigurationData],
  )

  const [catalogConfigurationState, setCatalogConfigurationState] = useState(
    normalizedCatalogConfiguration,
  )

  useEffect(() => {
    setCatalogConfigurationState(normalizedCatalogConfiguration)
  }, [normalizedCatalogConfiguration])

  const updateCatalogConfigurationMutation = useMutation(updateCatalogConfiguration, {
    onSuccess: () => {
      dispatch(
        notify({
          title: t('configuration.productCatalog.messages.updated'),
          status: 'success',
          dismissAfter: 2000,
        }),
      )
      refetchCatalogConfiguration()
    },
    onError: () => {
      dispatch(
        notify({
          title: t('configuration.productCatalog.messages.updateFailed'),
          status: 'error',
          dismissAfter: 4000,
        }),
      )
    },
  })

  const triggerSyncMutation = useMutation(syncCatalog, {
    onSuccess: (data) => {
      dispatch(
        notify({
          title: `${data.message}`,
          status: data.failedCount === 0 ? 'success' : 'warning',
          dismissAfter: 5000,
        }),
      )
    },
    onError: (error) => {
      dispatch(
        notify({
          title: t('configuration.productCatalog.messages.syncFailed'),
          message: error.response?.data?.message || error.message,
          status: 'error',
          dismissAfter: 5000,
        }),
      )
    },
  })

  const validateConfigMutation = useMutation(validatePlatformConfiguration, {
    onSuccess: (data) => {
      dispatch(
        notify({
          title: data.valid ? 'Configuration Valid' : 'Configuration Invalid',
          message: data.message,
          status: data.valid ? 'success' : 'error',
          dismissAfter: 4000,
        }),
      )
    },
  })

  const handleCatalogConfigurationChange = useCallback((section, key, value) => {
    setCatalogConfigurationState((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }))
  }, [])

  const handleCatalogConfigurationSave = useCallback(() => {
    updateCatalogConfigurationMutation.mutate(catalogConfigurationState)
  }, [updateCatalogConfigurationMutation, catalogConfigurationState])

  const handleTriggerSync = useCallback((platform) => {
    triggerSyncMutation.mutate({ platform })
  }, [triggerSyncMutation])

  const handleValidateConfig = useCallback((platform) => {
    validateConfigMutation.mutate(platform)
  }, [validateConfigMutation])

  const isCatalogConfigurationDirty = useMemo(
    () =>
      JSON.stringify(catalogConfigurationState) !==
      JSON.stringify(normalizedCatalogConfiguration),
    [catalogConfigurationState, normalizedCatalogConfiguration],
  )

  const isCheckoutConfigurationAvailable = Boolean(checkoutConfiguration?.id)
  const isCheckoutConfigurationLoading = isCheckoutConfigurationFetching
  const isCheckoutDirty = useMemo(
    () => JSON.stringify(checkoutConfigurationState) !== JSON.stringify(normalizedCheckoutConfiguration),
    [checkoutConfigurationState, normalizedCheckoutConfiguration],
  )

  const walletFields = useMemo(
    () =>
      WALLET_LIMIT_FIELDS.map((field) => ({
        ...field,
        title: t(field.titleKey),
        description: t(field.descriptionKey),
      })),
    [t],
  )

  const isWalletConfigurationAvailable = Boolean(walletConfiguration?.id)
  const isWalletConfigurationLoading = isWalletConfigurationFetching
  const isWalletDirty = useMemo(
    () => JSON.stringify(walletConfigurationState) !== JSON.stringify(normalizedWalletConfiguration),
    [walletConfigurationState, normalizedWalletConfiguration],
  )

  const tableChangeHandler = (pagination, filter, sorter) => {
    setCurrentPage(pageSize === pagination.pageSize ? pagination.current : 1)
    setPageSize(pagination.pageSize)
    setSortType(sorter.columnKey || null)
    setSortOrder(sorter.order)
  }

  const handleKeySearch = (e) => {
    setKeySearch(e.target.value)
    setCurrentPage(1)
  }

  const handleActiveFilterChange = (value) => {
    setActiveFilter(value)
    setCurrentPage(1)
  }

  const columns = useMemo(() => {
    return [
      {
        title: '№',
        dataIndex: 'id',
        key: 'id',
        align: 'center',
        width: '30px',
        render: (_, record, index) => (currentPage - 1) * pageSize + index + 1,
      },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title={t('configuration.table.key')} id="key" />
        ),
        dataIndex: 'key',
        key: 'key',
        sorter: true,
        width: 200,
        render: (key) => <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{key}</span>,
      },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title={t('configuration.table.valueEn')} id="valueEn" />
        ),
        dataIndex: 'valueEn',
        key: 'valueEn',
        sorter: true,
        width: 250,
        render: (valueEn) => (
          <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {valueEn}
          </div>
        ),
      },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title={t('configuration.table.valueAr')} id="valueAr" />
        ),
        dataIndex: 'valueAr',
        key: 'valueAr',
        sorter: true,
        width: 250,
        render: (valueAr) => (
          <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {valueAr}
          </div>
        ),
      },
      {
        title: t('configuration.table.status'),
        dataIndex: 'isActive',
        key: 'isActive',
        align: 'center',
        width: 100,
        render: (isActive) => (
          isActive ? <span>🟢 {t('app.active')}</span> : <span>🔴 {t('app.inactive')}</span>
        ),
      },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title={t('configuration.table.createdAt')} id="createdAt" />
        ),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (createdAt) => (createdAt ? moment(createdAt).format(DATE_TIME_FORMAT) : '-'),
        sorter: true,
        width: 150,
      },
    ]
  }, [t, isClientCanUpdateConfigurations, currentPage, pageSize])

  const filters = (
    <div className={styles.filtersRow}>
      <Select
        placeholder={t('configuration.filters.status')}
        style={{ width: 120, marginRight: 16 }}
        value={activeFilter}
        onChange={handleActiveFilterChange}
        allowClear
      >
        <Option value="true">{t('app.active')}</Option>
        <Option value="false">{t('app.inactive')}</Option>
      </Select>

      <Search
        placeholder={t('configuration.filters.searchKey')}
        style={{ width: 200 }}
        value={keySearch}
        onChange={handleKeySearch}
        allowClear
      />
    </div>
  )

  const isPaymentPanelLoading = isPaymentMethodsFetching || isPaymentConfigurationFetching

  return (
    <div className={styles.configurationPage}>
      <section className={styles.configurationAccordionSection}>
        <div className={styles.configurationAccordionHeader}>
          <h3>{t('configuration.accordion.title')}</h3>
          <p>{t('configuration.accordion.subtitle')}</p>
        </div>
        <div className={styles.scrollableAccordion}>
          <Collapse
            defaultActiveKey={[]}
            bordered={false}
            className={styles.configurationAccordion}
            collapsible="header"
          >
            <Panel
              header={
                <div className={styles.accordionPanelHeader}>
                  <span>{t('configuration.accordion.paymentMethods.title')}</span>
                  <span className={styles.accordionPanelCounter}>{paymentMethods.length}</span>
                </div>
              }
              key="payment-methods"
            >
              <PaymentMethodsPanel
                description={t('configuration.accordion.paymentMethods.description')}
                contexts={paymentContextOptions}
                methods={paymentMethods}
                onToggle={handlePaymentToggle}
                onSortOrderUpdate={handleSortOrderUpdate}
                onBatchSortOrderUpdate={handleBatchSortOrderUpdate}
                sortOrderSavingId={sortOrderSavingId}
                onIconUpload={handleIconUpload}
                iconUploadingId={iconUploadingId}
                loading={isPaymentPanelLoading}
                mutationLoading={updatePaymentMetadataMutation.isLoading}
                emptyText={t('configuration.paymentMethods.empty')}
                flagMap={PAYMENT_CONTEXT_FLAG_MAP}
                configuration={paymentConfigurationState}
              />
            </Panel>
            <Panel
              header={
                <div className={styles.accordionPanelHeader}>
                  <span>{t('configuration.wallet.title')}</span>
                </div>
              }
              key="wallet-configuration"
            >
              <WalletConfigurationPanel
                description={t('configuration.wallet.description')}
                fields={walletFields}
                values={walletConfigurationState}
                loading={isWalletConfigurationLoading}
                saving={updateWalletConfigurationMutation.isLoading}
                dirty={isWalletDirty}
                disabled={!isWalletConfigurationAvailable}
                missingConfigurationText={t('configuration.wallet.missingConfiguration')}
                onChange={handleWalletLimitChange}
                onSave={handleWalletConfigurationSave}
                minLabel={t('configuration.wallet.labels.min')}
                maxLabel={t('configuration.wallet.labels.max')}
                saveLabel={t('configuration.wallet.actions.save')}
              />
            </Panel>
            <Panel
              header={
                <div className={styles.accordionPanelHeader}>
                  <span>{t('configuration.checkout.title')}</span>
                </div>
              }
              key="checkout-configuration"
            >
              <CheckoutConfigurationPanel
                values={checkoutConfigurationState}
                loading={isCheckoutConfigurationLoading}
                saving={updateCheckoutConfigurationMutation.isLoading}
                dirty={isCheckoutDirty}
                disabled={!isCheckoutConfigurationAvailable}
                missingConfigurationText={t('configuration.checkout.missingConfiguration')}
                onChange={handleCheckoutConfigurationChange}
                onSave={handleCheckoutConfigurationSave}
                labels={{
                  checkoutMinAmount: t('configuration.checkout.fields.checkoutMinAmount.title'),
                  deliveryDateMessage: t('configuration.checkout.fields.deliveryDateMessage.title'),
                  save: t('configuration.checkout.actions.save'),
                }}
              />
            </Panel>
            <Panel
              header={
                <div className={styles.accordionPanelHeader}>
                  <span>{t('configuration.marketingEmail.title')}</span>
                </div>
              }
              key="marketing-email-configuration"
            >
              <MarketingEmailConfigurationPanel
                value={marketingEmailState}
                loading={isMarketingEmailConfigurationFetching}
                saving={updateMarketingEmailMutation.isLoading}
                dirty={isMarketingEmailDirty}
                disabled={!isMarketingEmailConfigurationAvailable}
                missingConfigurationText={t('configuration.marketingEmail.missingConfiguration')}
                onChange={handleMarketingEmailChange}
                onSave={handleMarketingEmailSave}
                label={t('configuration.marketingEmail.fields.email.title')}
                saveLabel={t('configuration.marketingEmail.actions.save')}
              />
            </Panel>
            <Panel
              header={
                <div className={styles.accordionPanelHeader}>
                  <span>{t('configuration.loyaltyReferral.title')}</span>
                </div>
              }
              key="loyalty-referral-configuration"
            >
              <LoyaltyReferralConfigurationPanel
                values={loyaltyReferralConfigurationState}
                loading={isLoyaltyReferralConfigurationLoading}
                saving={updateLoyaltyReferralConfigurationMutation.isLoading}
                dirty={isLoyaltyReferralDirty}
                disabled={!isLoyaltyReferralConfigurationAvailable}
                missingConfigurationText={t('configuration.loyaltyReferral.missingConfiguration')}
                onChange={handleLoyaltyReferralConfigurationChange}
                onSave={handleLoyaltyReferralConfigurationSave}
                shareMessageValidationError={shareMessageValidationError}
                t={t}
              />
            </Panel>
            <Panel
              header={
                <div className={styles.accordionPanelHeader}>
                  <span>{t('configuration.productCatalog.title') || 'Product Catalog Sync'}</span>
                </div>
              }
              key="product-catalog-configuration"
            >
              <ProductCatalogConfigurationPanel
                values={catalogConfigurationState}
                loading={isCatalogConfigurationFetching}
                saving={updateCatalogConfigurationMutation.isLoading}
                syncing={triggerSyncMutation.isLoading}
                validating={validateConfigMutation.isLoading}
                dirty={isCatalogConfigurationDirty}
                onChange={handleCatalogConfigurationChange}
                onSave={handleCatalogConfigurationSave}
                onSync={handleTriggerSync}
                onValidate={handleValidateConfig}
                t={t}
              />
            </Panel>
            <Panel
              header={
                <div className={styles.accordionPanelHeader}>
                  <span>{t('configuration.contactInfo.title')}</span>
                </div>
              }
              key="contact-info"
            >
              <ContactInfoPanel
                values={contactInfoState}
                loading={isContactInfoFetching}
                saving={updateContactInfoMutation.isLoading}
                dirty={isContactInfoDirty}
                onChange={handleContactInfoChange}
                onSave={handleContactInfoSave}
                t={t}
              />
            </Panel>
          </Collapse>
        </div>
      </section>

      {/* <PageTable
        action={
          <div className={styles.filter}>
            {filters}
            {isClientCanCreateConfigurations && (
              <Button type="primary" onClick={() => navigate(`/main/configuration/add`)}>
                <Icon icon={ICONS.PLUS} className="btn-icon-suffix" />
                <span className="btn-text">{t('configuration.newConfiguration')}</span>
              </Button>
            )}
          </div>
        }
        baseData={configurationList?.result || []}
        columns={columns}
        loading={isFetching}
        onChange={tableChangeHandler}
        onRow={
          isClientCanUpdateConfigurations
            ? (record) => ({
                onClick: () => navigate(`/main/configuration/${record.id}`),
              })
            : null
        }
        pageTitle={t('configuration.title')}
        pagination={{
          pageSize,
          current: currentPage,
          total: configurationList?.total,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          showSizeChanger: configurationList?.total >= 10 || true,
        }}
      /> */}
    </div>
  )
}

const ACCEPTED_ICON_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml']
const MAX_ICON_SIZE_MB = 2
const DRAGGABLE_PAYMENT_METHOD = 'DRAGGABLE_PAYMENT_METHOD'

const ContactInfoPanel = ({ values, loading, saving, dirty, onChange, onSave, t }) => {
  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <Spin />
      </div>
    )
  }

  return (
    <div style={{ padding: 16, maxWidth: 520 }}>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>
          {t('configuration.contactInfo.fields.address.title')}
        </label>
        <Input
          value={values.address}
          onChange={(e) => onChange('address', e.target.value)}
          placeholder={t('configuration.contactInfo.fields.address.placeholder')}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>
          {t('configuration.contactInfo.fields.phone.title')}
        </label>
        <Input
          value={values.phone}
          onChange={(e) => onChange('phone', e.target.value)}
          placeholder={t('configuration.contactInfo.fields.phone.placeholder')}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>
          {t('configuration.contactInfo.fields.whatsApp.title')}
        </label>
        <Input
          value={values.whatsApp}
          onChange={(e) => onChange('whatsApp', e.target.value)}
          placeholder={t('configuration.contactInfo.fields.whatsApp.placeholder')}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>
          {t('configuration.contactInfo.fields.instagram.title')}
        </label>
        <Input
          value={values.instagram}
          onChange={(e) => onChange('instagram', e.target.value)}
          placeholder={t('configuration.contactInfo.fields.instagram.placeholder')}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>
          {t('configuration.contactInfo.fields.twitter.title')}
        </label>
        <Input
          value={values.twitter}
          onChange={(e) => onChange('twitter', e.target.value)}
          placeholder={t('configuration.contactInfo.fields.twitter.placeholder')}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>
          {t('configuration.contactInfo.fields.email.title')}
        </label>
        <Input
          value={values.email}
          onChange={(e) => onChange('email', e.target.value)}
          placeholder={t('configuration.contactInfo.fields.email.placeholder')}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>
          {t('configuration.contactInfo.fields.facebook.title')}
        </label>
        <Input
          value={values.facebook}
          onChange={(e) => onChange('facebook', e.target.value)}
          placeholder={t('configuration.contactInfo.fields.facebook.placeholder')}
        />
      </div>

      <Button type="primary" disabled={!dirty} loading={saving} onClick={onSave}>
        <span className="btn-text">{t('configuration.contactInfo.actions.save')}</span>
      </Button>
    </div>
  )
}

const PaymentMethodsPanel = ({
  description,
  contexts,
  methods,
  onToggle,
  onSortOrderUpdate,
  onBatchSortOrderUpdate,
  sortOrderSavingId,
  onIconUpload,
  iconUploadingId,
  loading,
  mutationLoading,
  emptyText,
  flagMap,
  configuration,
}) => {
  const { t } = useTranslation()
  const [orderedMethods, setOrderedMethods] = useState(() => methods ?? [])
  const isSortable = Boolean(onSortOrderUpdate)
  const originalSortOrderMap = useMemo(() => {
    if (!methods) {
      return new Map()
    }

    return new Map(methods.map((method) => [method.id, method.sortOrder ?? null]))
  }, [methods])

  useEffect(() => {
    setOrderedMethods(methods ?? [])
  }, [methods])

  const moveMethod = useCallback((dragIndex, hoverIndex) => {
    if (dragIndex === hoverIndex) {
      return
    }

    setOrderedMethods((prev) => {
      if (!Array.isArray(prev) || !prev.length) {
        return prev
      }

      const dragItem = prev[dragIndex]
      if (!dragItem) {
        return prev
      }

      const next = update(prev, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragItem],
        ],
      })

      return next.map((method, index) => ({
        ...method,
        sortOrder: index + 1,
      }))
    })
  }, [])

  const handleDropCommit = useCallback(
    (dragId, dragIndex) => {
      if (!onBatchSortOrderUpdate || !Array.isArray(orderedMethods) || !orderedMethods.length) {
        return
      }

      const itemsToUpdate = []

      orderedMethods.forEach((method, index) => {
        const nextOrder = index + 1
        const previousOrder = originalSortOrderMap.get(method.id)

        if (previousOrder !== nextOrder) {
          itemsToUpdate.push({
            id: method.id,
            sortOrder: nextOrder,
          })
        }
      })

      if (itemsToUpdate.length > 0) {
        onBatchSortOrderUpdate(itemsToUpdate)
      }
    },
    [onBatchSortOrderUpdate, orderedMethods, originalSortOrderMap],
  )

  const handleIconBeforeUpload = useCallback(
    (methodId, file) => {
      if (!ACCEPTED_ICON_TYPES.includes(file.type)) {
        message.error(t('configuration.paymentMethods.errors.invalidIconFormat'))
        return Upload.LIST_IGNORE
      }

      const isWithinSize = file.size / 1024 / 1024 <= MAX_ICON_SIZE_MB
      if (!isWithinSize) {
        message.error(
          t('configuration.paymentMethods.errors.invalidIconSize', {
            size: MAX_ICON_SIZE_MB,
          }),
        )
        return Upload.LIST_IGNORE
      }

      onIconUpload?.(methodId, file)
      return false
    },
    [onIconUpload, t],
  )

  if (loading) {
    return (
      <div className={styles.paymentPanelLoader}>
        <Spin />
      </div>
    )
  }

  if (!orderedMethods || orderedMethods.length === 0) {
    return <Empty description={emptyText} />
  }

  const methodRows = orderedMethods.map((method, index) => {
    const header = (
      <>
        <div className={styles.paymentMethodHeader}>
          <div className={styles.paymentMethodInfo}>
            {isSortable && (
              <span className={styles.paymentMethodDragHandle}>
                <Icon icon={ICONS.MENU_OUTLINED} />
              </span>
            )}
            <Upload
              accept={ACCEPTED_ICON_TYPES.join(',')}
              showUploadList={false}
              beforeUpload={(file) => handleIconBeforeUpload(method.id, file)}
              disabled={iconUploadingId === method.id || !onIconUpload}
            >
              <div
                className={styles.paymentMethodIconUploadTrigger}
                role="button"
                tabIndex={0}
              >
                {method.iconUrl ? (
                  <img
                    src={method.iconUrl}
                    alt={`${method.name} icon`}
                    className={styles.paymentMethodIcon}
                  />
                ) : (
                  <div className={styles.paymentMethodIconPlaceholder}>
                    <Icon icon={ICONS.IMAGE} />
                  </div>
                )}
                {iconUploadingId === method.id && (
                  <div className={styles.paymentMethodIconOverlay}>
                    <Spin size="small" />
                  </div>
                )}
              </div>
            </Upload>
            <div className={styles.paymentMethodText}>
              <div className={styles.paymentMethodName}>{method.name}</div>
            </div>
          </div>

          <div className={styles.paymentMethodHeaderActions}>
            {sortOrderSavingId === method.id && <Spin size="small" />}
          </div>
        </div>

        <div className={styles.paymentMethodSwitches}>
          {contexts.map((context) => {
            const usageFlagKey = flagMap[context.key]
            
            // Read from LOV entity usageFlags instead of configuration metadata
            const isEnabled = Boolean(method.usageFlags?.[usageFlagKey])

            const methodKey = method.name
            let shouldShow = true

            if (methodKey === 'E_WALLET' || methodKey === 'E-WALLET') {
              shouldShow = ['order', 'mosque', 'subscription'].includes(context.key)
            } else if (['KNET', 'CREDIT_CARD', 'APPLE_PAY'].includes(methodKey)) {
              shouldShow = context.key !== 'coupon'
            } else if (methodKey === 'CASH') {
              shouldShow = context.key === 'order'
            } else if (methodKey === 'E-COUPON' || methodKey === 'COUPON') {
              shouldShow = context.key === 'coupon'
            }

            if (!shouldShow) {
              return null
            }

            return (
              <div key={`${method.id}-${context.key}`} className={styles.paymentMethodSwitch}>
                <span>{context.label}</span>
                <Switch
                  checked={isEnabled}
                  loading={mutationLoading}
                  onChange={(checked) => onToggle(method, context.key, checked)}
                />
              </div>
            )
          })}
        </div>
      </>
    )

    if (!isSortable) {
      return (
        <div key={method.id} className={styles.paymentMethodRow}>
          {header}
        </div>
      )
    }

    return (
      <SortablePaymentMethodRow
        key={method.id}
        id={method.id}
        index={index}
        moveMethod={moveMethod}
        onDrop={handleDropCommit}
      >
        {header}
      </SortablePaymentMethodRow>
    )
  })

  return (
    <div className={styles.paymentPanel}>
      {description && <p className={styles.paymentPanelDescription}>{description}</p>}

      <div className={styles.paymentMethodList}>
        {isSortable ? <DndProvider backend={HTML5Backend}>{methodRows}</DndProvider> : methodRows}
      </div>
    </div>
  )
}

const SortablePaymentMethodRow = ({ id, index, moveMethod, onDrop, children }) => {
  const ref = useRef(null)
  const [, drop] = useDrop({
    accept: DRAGGABLE_PAYMENT_METHOD,
    hover: (item) => {
      if (!ref.current) {
        return
      }

      const dragIndex = item.index
      if (dragIndex === index) {
        return
      }

      moveMethod(dragIndex, index)
      item.index = index
    },
    drop: (item) => {
      onDrop?.(item.id, item.index)
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: DRAGGABLE_PAYMENT_METHOD,
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      className={`${styles.paymentMethodRow} ${isDragging ? styles.paymentMethodRowDragging : ''}`}
    >
      {children}
    </div>
  )
}

const WalletConfigurationPanel = ({
  description,
  fields,
  values,
  loading,
  saving,
  dirty,
  disabled,
  missingConfigurationText,
  onChange,
  onSave,
  minLabel,
  maxLabel,
  saveLabel,
}) => {
  if (loading) {
    return (
      <div className={styles.walletPanelLoader}>
        <Spin />
      </div>
    )
  }

  if (disabled) {
    return <Empty description={missingConfigurationText} />
  }

  return (
    <div className={styles.walletPanel}>
      {description && <p className={styles.walletPanelDescription}>{description}</p>}

      <div className={styles.walletFieldList}>
        {fields.map((field) => {
          const fieldValue = values[field.key] || { min: 0, max: 0 }

          return (
            <div key={field.key} className={styles.walletField}>
              <div className={styles.walletFieldHeader}>
                <div className={styles.walletFieldTitle}>{field.title}</div>
                {field.description && <p className={styles.walletFieldDescription}>{field.description}</p>}
              </div>

              <div className={styles.walletFieldInputs}>
                <div className={styles.walletFieldInput}>
                  <label>{minLabel}</label>
                  <InputNumber
                    min={0}
                    precision={3}
                    value={fieldValue.min}
                    onChange={(value) => onChange(field.key, 'min', value)}
                  />
                </div>
                <div className={styles.walletFieldInput}>
                  <label>{maxLabel}</label>
                  <InputNumber
                    min={fieldValue.min}
                    precision={3}
                    value={fieldValue.max}
                    onChange={(value) => onChange(field.key, 'max', value)}
                  />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className={styles.walletSaveBar}>
        <Button type="primary" disabled={!dirty || saving} loading={saving} onClick={onSave}>
          {saveLabel}
        </Button>
      </div>
    </div>
  )
}

const CheckoutConfigurationPanel = ({
  values,
  loading,
  saving,
  dirty,
  disabled,
  missingConfigurationText,
  onChange,
  onSave,
  labels,
}) => {
  if (loading) {
    return (
      <div className={styles.walletPanelLoader}>
        <Spin />
      </div>
    )
  }

  if (disabled) {
    return <Empty description={missingConfigurationText} />
  }

  return (
    <div className={styles.walletPanel}>
      <div className={styles.walletFieldList}>
        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>{labels.checkoutMinAmount}</div>
          </div>
          <div className={styles.walletFieldInputs}>
            <div className={styles.walletFieldInput}>
              <InputNumber
                min={0}
                precision={3}
                style={{ width: '100%' }}
                value={values.checkoutMinAmount}
                onChange={(value) => onChange('checkoutMinAmount', value)}
              />
            </div>
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>{labels.deliveryDateMessage}</div>
          </div>
          <div className={styles.walletFieldInputs} style={{ flexDirection: 'column', gap: 10 }}>
            <div className={styles.walletFieldInput} style={{ width: '100%' }}>
              <label>English</label>
              <Input
                value={values.deliveryDateMessage.en}
                onChange={(e) => onChange('deliveryDateMessage', e.target.value, 'en')}
              />
            </div>
            <div className={styles.walletFieldInput} style={{ width: '100%' }}>
              <label>Arabic</label>
              <Input
                value={values.deliveryDateMessage.ar}
                onChange={(e) => onChange('deliveryDateMessage', e.target.value, 'ar')}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.walletSaveBar}>
        <Button type="primary" disabled={!dirty || saving} loading={saving} onClick={onSave}>
          {labels.save}
        </Button>
      </div>
    </div>
  )
}

const MarketingEmailConfigurationPanel = ({
  value,
  loading,
  saving,
  dirty,
  disabled,
  missingConfigurationText,
  onChange,
  onSave,
  label,
  saveLabel,
}) => {
  if (loading) {
    return (
      <div className={styles.walletPanelLoader}>
        <Spin />
      </div>
    )
  }

  if (disabled) {
    return <Empty description={missingConfigurationText} />
  }

  return (
    <div className={styles.walletPanel}>
      <div className={styles.walletFieldList}>
        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>{label}</div>
            <p className={styles.walletFieldDescription}>
              Email address for marketing communications and mosque request notifications
            </p>
          </div>
          <div className={styles.walletFieldInputs}>
            <div className={styles.walletFieldInput} style={{ width: '100%' }}>
              <Input
                type="email"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Enter marketing email address"
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.walletSaveBar}>
        <Button type="primary" disabled={!dirty || saving} loading={saving} onClick={onSave}>
          {saveLabel}
        </Button>
      </div>
    </div>
  )
}

const LoyaltyReferralConfigurationPanel = ({
  values,
  loading,
  saving,
  dirty,
  disabled,
  missingConfigurationText,
  onChange,
  onSave,
  shareMessageValidationError,
  t,
}) => {
  if (loading) {
    return (
      <div className={styles.walletPanelLoader}>
        <Spin />
      </div>
    )
  }

  if (disabled) {
    return <Empty description={missingConfigurationText} />
  }

  return (
    <div className={styles.walletPanel}>
      <div className={styles.walletFieldList}>
        {/* Loyalty Points Section */}
        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.sections.loyalty')}
            </div>
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.fields.loyaltyEnabled.title')}
            </div>
            <p className={styles.walletFieldDescription}>
              {t('configuration.loyaltyReferral.fields.loyaltyEnabled.description')}
            </p>
          </div>
          <div className={styles.walletFieldInputs}>
            <Switch
              checked={values.loyaltyEnabled}
              onChange={(checked) => onChange('loyaltyEnabled', checked)}
            />
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.fields.pointsPerKwd.title')}
            </div>
            <p className={styles.walletFieldDescription}>
              {t('configuration.loyaltyReferral.fields.pointsPerKwd.description')}
            </p>
          </div>
          <div className={styles.walletFieldInputs}>
            <InputNumber
              min={1}
              precision={0}
              style={{ width: '200px' }}
              value={values.pointsPerKwd}
              onChange={(value) => onChange('pointsPerKwd', value)}
              disabled={!values.loyaltyEnabled}
            />
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.fields.redemptionPointsPerKwd.title')}
            </div>
            <p className={styles.walletFieldDescription}>
              {t('configuration.loyaltyReferral.fields.redemptionPointsPerKwd.description')}
            </p>
          </div>
          <div className={styles.walletFieldInputs}>
            <InputNumber
              min={1}
              precision={0}
              style={{ width: '200px' }}
              value={values.redemptionPointsPerKwd}
              onChange={(value) => onChange('redemptionPointsPerKwd', value)}
              disabled={!values.loyaltyEnabled}
            />
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.fields.earnPointMinOrderValue.title')}
            </div>
            <p className={styles.walletFieldDescription}>
              {t('configuration.loyaltyReferral.fields.earnPointMinOrderValue.description')}
            </p>
          </div>
          <div className={styles.walletFieldInputs}>
            <InputNumber
              min={0}
              precision={3}
              style={{ width: '200px' }}
              value={values.earnPointMinOrderValue}
              onChange={(value) => onChange('earnPointMinOrderValue', value)}
              disabled={!values.loyaltyEnabled}
            />
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.fields.minimumRedeemPointsForOrder.title')}
            </div>
            <p className={styles.walletFieldDescription}>
              {t('configuration.loyaltyReferral.fields.minimumRedeemPointsForOrder.description')}
            </p>
          </div>
          <div className={styles.walletFieldInputs}>
            <InputNumber
              min={0}
              precision={0}
              style={{ width: '200px' }}
              value={values.minimumRedeemPointsForOrder}
              onChange={(value) => onChange('minimumRedeemPointsForOrder', value)}
              disabled={!values.loyaltyEnabled}
            />
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.fields.pointsExpiryInMonths.title')}
            </div>
            <p className={styles.walletFieldDescription}>
              {t('configuration.loyaltyReferral.fields.pointsExpiryInMonths.description')}
            </p>
          </div>
          <div className={styles.walletFieldInputs}>
            <InputNumber
              min={1}
              max={36}
              precision={0}
              style={{ width: '200px' }}
              value={values.pointsExpiryInMonths}
              onChange={(value) => onChange('pointsExpiryInMonths', value)}
              disabled={!values.loyaltyEnabled}
            />
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.fields.completeProfilePoints.title')}
            </div>
            <p className={styles.walletFieldDescription}>
              {t('configuration.loyaltyReferral.fields.completeProfilePoints.description')}
            </p>
          </div>
          <div className={styles.walletFieldInputs}>
            <InputNumber
              min={0}
              precision={0}
              style={{ width: '200px' }}
              value={values.completeProfilePoints}
              onChange={(value) => onChange('completeProfilePoints', value)}
              disabled={!values.loyaltyEnabled}
            />
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.fields.maxRedeemPercentage.title')}
            </div>
            <p className={styles.walletFieldDescription}>
              {t('configuration.loyaltyReferral.fields.maxRedeemPercentage.description')}
            </p>
          </div>
          <div className={styles.walletFieldInputs}>
            <InputNumber
              min={1}
              max={100}
              precision={0}
              style={{ width: '200px' }}
              value={values.maxRedeemPercentage}
              onChange={(value) => onChange('maxRedeemPercentage', value)}
              disabled={!values.loyaltyEnabled}
              addonAfter="%"
            />
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.fields.disableLoyaltyWithPromocode.title')}
            </div>
            <p className={styles.walletFieldDescription}>
              {t('configuration.loyaltyReferral.fields.disableLoyaltyWithPromocode.description')}
            </p>
          </div>
          <div className={styles.walletFieldInputs}>
            <Switch
              checked={values.disableLoyaltyWithPromocode}
              onChange={(checked) => onChange('disableLoyaltyWithPromocode', checked)}
              disabled={!values.loyaltyEnabled}
            />
          </div>
        </div>

        {/* Referral Section */}
        <div className={styles.walletField} style={{ marginTop: '30px' }}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.sections.referral')}
            </div>
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.fields.referralEnabled.title')}
            </div>
            <p className={styles.walletFieldDescription}>
              {t('configuration.loyaltyReferral.fields.referralEnabled.description')}
            </p>
          </div>
          <div className={styles.walletFieldInputs}>
            <Switch
              checked={values.referralEnabled}
              onChange={(checked) => onChange('referralEnabled', checked)}
            />
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.fields.newCustomerPoints.title')}
            </div>
            <p className={styles.walletFieldDescription}>
              {t('configuration.loyaltyReferral.fields.newCustomerPoints.description')}
            </p>
          </div>
          <div className={styles.walletFieldInputs}>
            <InputNumber
              min={0}
              precision={0}
              style={{ width: '200px' }}
              value={values.newCustomerPoints}
              onChange={(value) => onChange('newCustomerPoints', value)}
              disabled={!values.referralEnabled}
            />
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.fields.referrerPoints.title')}
            </div>
            <p className={styles.walletFieldDescription}>
              {t('configuration.loyaltyReferral.fields.referrerPoints.description')}
            </p>
          </div>
          <div className={styles.walletFieldInputs}>
            <InputNumber
              min={0}
              precision={0}
              style={{ width: '200px' }}
              value={values.referrerPoints}
              onChange={(value) => onChange('referrerPoints', value)}
              disabled={!values.referralEnabled}
            />
          </div>
        </div>

        <div className={styles.walletField}>
          <div className={styles.walletFieldHeader}>
            <div className={styles.walletFieldTitle}>
              {t('configuration.loyaltyReferral.fields.shareMessage.title')}
            </div>
            <p className={styles.walletFieldDescription}>
              {t('configuration.loyaltyReferral.fields.shareMessage.description')}
            </p>
          </div>
          <div className={styles.walletFieldInputs} style={{ flexDirection: 'column', gap: 10, width: '100%' }}>
            <div className={styles.walletFieldInput} style={{ width: '100%' }}>
              <label>English</label>
              <Input.TextArea
                rows={3}
                value={values.shareMessageEn}
                onChange={(e) => onChange('shareMessageEn', e.target.value)}
                disabled={!values.referralEnabled}
                placeholder={'Use {{REFERRAL_CODE}} as placeholder for the user\'s referral code'}
              />
            </div>
            <div className={styles.walletFieldInput} style={{ width: '100%' }}>
              <label>Arabic</label>
              <Input.TextArea
                rows={3}
                value={values.shareMessageAr}
                onChange={(e) => onChange('shareMessageAr', e.target.value)}
                disabled={!values.referralEnabled}
                placeholder={'Use {{REFERRAL_CODE}} as placeholder for the user\'s referral code'}
              />
            </div>
            {shareMessageValidationError && (
              <div style={{ color: '#ff4d4f', fontSize: 13, marginTop: 4 }}>
                {shareMessageValidationError}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.walletSaveBar}>
        <Button
          type="primary"
          disabled={!dirty || saving || !!shareMessageValidationError}
          loading={saving}
          onClick={onSave}
        >
          {t('configuration.loyaltyReferral.actions.save')}
        </Button>
      </div>
    </div>
  )
}

const ProductCatalogConfigurationPanel = ({
  values,
  loading,
  saving,
  syncing,
  validating,
  dirty,
  onChange,
  onSave,
  onSync,
  onValidate,
  t,
}) => {
  if (loading) {
    return (
      <div className={styles.walletPanelLoader}>
        <Spin />
      </div>
    )
  }

  return (
    <div className={styles.walletPanel}>
      <p className={styles.walletPanelDescription}>
        {t('configuration.productCatalog.description') || 
          'Configure product catalog synchronization with Meta (Facebook) and Google Merchant Center.'}
      </p>

      {/* Meta Configuration */}
      <div className={styles.walletField}>
        <div className={styles.walletFieldHeader}>
          <div className={styles.walletFieldTitle}>
            {t('configuration.productCatalog.sections.meta') || 'Meta (Facebook) Catalog'}
          </div>
        </div>
        <div className={styles.walletFieldInputs} style={{ flexDirection: 'column', gap: 16 }}>
          <div className={styles.walletFieldInput} style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <Switch
              checked={values.meta?.enabled}
              onChange={(checked) => onChange('meta', 'enabled', checked)}
            />
            <span>{t('configuration.productCatalog.fields.enabled') || 'Enable Meta Catalog Sync'}</span>
          </div>
          
          {values.meta?.enabled && (
            <>
              <div className={styles.walletFieldInput} style={{ width: '100%' }}>
                <label>{t('configuration.productCatalog.fields.businessId') || 'Business ID'}</label>
                <Input
                  value={values.meta?.businessId}
                  onChange={(e) => onChange('meta', 'businessId', e.target.value)}
                  placeholder="Enter Meta Business ID"
                />
              </div>
              <div className={styles.walletFieldInput} style={{ width: '100%' }}>
                <label>{t('configuration.productCatalog.fields.catalogId') || 'Catalog ID'}</label>
                <Input
                  value={values.meta?.catalogId}
                  onChange={(e) => onChange('meta', 'catalogId', e.target.value)}
                  placeholder="Enter Meta Catalog ID"
                />
              </div>
              <div className={styles.walletFieldInput} style={{ width: '100%' }}>
                <label>{t('configuration.productCatalog.fields.accessToken') || 'Access Token'}</label>
                <Input
                  value={values.meta?.accessToken}
                  onChange={(e) => onChange('meta', 'accessToken', e.target.value)}
                  placeholder="Enter Meta Access Token"
                />
              </div>
              <div className={styles.walletFieldInput} style={{ width: '100%' }}>
                <label>{t('configuration.productCatalog.fields.storeUrl') || 'Store URL'}</label>
                <Input
                  value={values.meta?.storeUrl}
                  onChange={(e) => onChange('meta', 'storeUrl', e.target.value)}
                  placeholder="https://your-store.com"
                />
              </div>
              <div className={styles.walletFieldInput} style={{ width: '100%' }}>
                <label>{t('configuration.productCatalog.fields.brandName') || 'Brand Name'}</label>
                <Input
                  value={values.meta?.brandName}
                  onChange={(e) => onChange('meta', 'brandName', e.target.value)}
                  placeholder="Abraaj Water"
                />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <Button
                  onClick={() => onValidate(CatalogPlatform.META)}
                  loading={validating}
                  disabled={!values.meta?.catalogId || !values.meta?.accessToken}
                >
                  {t('configuration.productCatalog.actions.validate') || 'Validate Configuration'}
                </Button>
                <Button
                  type="primary"
                  onClick={() => onSync(CatalogPlatform.META)}
                  loading={syncing}
                  disabled={!values.meta?.catalogId || !values.meta?.accessToken}
                >
                  {t('configuration.productCatalog.actions.syncNow') || 'Sync Now'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Google Configuration */}
      <div className={styles.walletField}>
        <div className={styles.walletFieldHeader}>
          <div className={styles.walletFieldTitle}>
            {t('configuration.productCatalog.sections.google') || 'Google Merchant Center'}
          </div>
        </div>
        <div className={styles.walletFieldInputs} style={{ flexDirection: 'column', gap: 16 }}>
          <div className={styles.walletFieldInput} style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <Switch
              checked={values.google?.enabled}
              onChange={(checked) => onChange('google', 'enabled', checked)}
            />
            <span>{t('configuration.productCatalog.fields.enabled') || 'Enable Google Merchant Center Sync'}</span>
          </div>
          
          {values.google?.enabled && (
            <>
              <div className={styles.walletFieldInput} style={{ width: '100%' }}>
                <label>{t('configuration.productCatalog.fields.merchantId') || 'Merchant ID'}</label>
                <Input
                  value={values.google?.merchantId}
                  onChange={(e) => onChange('google', 'merchantId', e.target.value)}
                  placeholder="Enter Google Merchant Center ID"
                />
              </div>
              <div className={styles.walletFieldInput} style={{ width: '100%' }}>
                <label>{t('configuration.productCatalog.fields.serviceAccount') || 'Service Account Credentials (JSON)'}</label>
                <Input.TextArea
                  value={values.google?.serviceAccountCredentials}
                  onChange={(e) => onChange('google', 'serviceAccountCredentials', e.target.value)}
                  placeholder="Paste your Google Service Account JSON here"
                  rows={4}
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
              <div className={styles.walletFieldInput} style={{ width: '100%' }}>
                <label>{t('configuration.productCatalog.fields.storeUrl') || 'Store URL'}</label>
                <Input
                  value={values.google?.storeUrl}
                  onChange={(e) => onChange('google', 'storeUrl', e.target.value)}
                  placeholder="https://your-store.com"
                />
              </div>
              <div className={styles.walletFieldInput} style={{ width: '100%' }}>
                <label>{t('configuration.productCatalog.fields.brandName') || 'Brand Name'}</label>
                <Input
                  value={values.google?.brandName}
                  onChange={(e) => onChange('google', 'brandName', e.target.value)}
                  placeholder="Abraaj Water"
                />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <Button
                  onClick={() => onValidate(CatalogPlatform.GOOGLE)}
                  loading={validating}
                  disabled={!values.google?.merchantId || !values.google?.serviceAccountCredentials}
                >
                  {t('configuration.productCatalog.actions.validate') || 'Validate Configuration'}
                </Button>
                <Button
                  type="primary"
                  onClick={() => onSync(CatalogPlatform.GOOGLE)}
                  loading={syncing}
                  disabled={!values.google?.merchantId || !values.google?.serviceAccountCredentials}
                >
                  {t('configuration.productCatalog.actions.syncNow') || 'Sync Now'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className={styles.walletSaveBar}>
        <Button
          type="primary"
          disabled={!dirty || saving}
          loading={saving}
          onClick={onSave}
        >
          {t('configuration.productCatalog.actions.save') || 'Save Configuration'}
        </Button>
      </div>
    </div>
  )
}

export default Configuration
